import { format, parse } from 'date-fns';
import { useConfig } from '../context/ConfigContext';
import { getCourtLabel } from '../utils/bookingUtils';
import {
  getPackageBookingPrice,
  getPackageBookingCourts,
  describePackageBookingTime,
} from '../utils/packageUtils';
import { formatMoney } from '../utils/currencyUtil';

/**
 * The summary card for a booking made through the event-package flow: date,
 * package name, the block of hours, its one price, and the courts it takes
 * over.
 *
 * Shared by every screen that recaps a booking (details, checkout,
 * confirmation) rather than repeated in each, because a package must never be
 * itemised per hour: the club sells a 4-hour block at one price, so listing
 * five ₱300 court-hours shows a rate the guest was never charged, and the
 * hours shown would include the cleanup slot they aren't paying for.
 *
 * Uses the same uif-* classes as the hourly breakdown it replaces, so it sits
 * in the same card with no per-screen styling.
 *
 * @param {object} props
 * @param {object} props.bookingData - a saved booking record
 * @param {string} [props.dateStr] - override the date shown (multi-date recaps)
 */
function PackageBookingBox({ bookingData, dateStr = null }) {
  const { config } = useConfig();
  if (!bookingData) return null;

  const date = dateStr || bookingData.date;
  const dateLabel = date ? format(parse(date, 'yyyy-MM-dd', new Date()), 'EEEE, MMMM d, yyyy') : '';
  const courtNames = getPackageBookingCourts(bookingData)
    .map(court => getCourtLabel(config, court))
    .join(', ');

  return (
    <div className="uif-booking-box">
      <h3>{dateLabel}</h3>
      <div className="uif-court-lbl">{bookingData.packageName || 'Package'}</div>
      <div className="uif-row">
        <span className="uif-time">{describePackageBookingTime(bookingData)}</span>
        <span className="uif-fee">{formatMoney(getPackageBookingPrice(bookingData), config)}</span>
      </div>
      {courtNames && (
        <div className="uif-row">
          <span className="uif-time">{courtNames}</span>
        </div>
      )}
    </div>
  );
}

export default PackageBookingBox;
